// Contratos del motor de sincronización. El motor no sabe nada de Dexie ni de Supabase:
// recibe un SyncStore (base local) y un SyncTransport (servidor). Eso permite testearlo
// con dobles en memoria y, a futuro, usar SQLite o un transporte nulo (versión 100% local).

/** Tablas que el dispositivo baja del servidor (subconjunto de app.sync_tables() en SQL). */
export const SYNC_TABLES = [
  'businesses',
  'memberships',
  'devices',
  'categories',
  'suppliers',
  'products',
  'product_variants',
  'variant_costs',
  'stock_levels',
  'purchases',
  'purchase_items',
  'customers',
  'cash_sessions',
  'customer_balances',
  // Cambios rechazados en cuarentena: cada uno baja los suyos (el dueño, todos) para enterarse
  // de si el dueño los aplicó o se descartaron.
  'sync_rejections',
  // Cajas (compartidas por todos los dispositivos del negocio).
  'registers',
] as const;

/**
 * Tablas que el dispositivo envía pero no baja: los movimientos de stock crecen sin
 * límite, así que cada dispositivo baja el resumen (stock_levels) y el historial se
 * consulta online.
 */
export const PUSH_ONLY_TABLES = ['stock_movements', 'sales', 'cash_movements', 'account_entries'] as const;

/**
 * Tablas cuyas operaciones puede enviar cualquier usuario del negocio, no solo su autor.
 * Si un empleado vendió sin conexión y después entra el dueño en ese dispositivo, las ventas
 * no esperan a que vuelva el empleado: el servidor las registra a nombre de quien vendió.
 */
export const SHARED_PUSH_TABLES: readonly SyncTable[] = ['sales'];

export type SyncTable = (typeof SYNC_TABLES)[number] | (typeof PUSH_ONLY_TABLES)[number];

export type Row = Record<string, unknown> & { id: string };

export type OutboxStatus = 'pending' | 'rejected';

export interface OutboxOp {
  /** Autoincremental local: define el orden FIFO. */
  seq?: number;
  op_id: string;
  business_id: string;
  table: SyncTable;
  /** insert: fila nueva (idempotente por id). patch: solo los campos cambiados. */
  kind: 'insert' | 'patch';
  row_id: string;
  payload: Record<string, unknown>;
  /**
   * Solo en patches: el valor que tenía cada campo en el dispositivo antes de cambiarlo. Si en
   * el servidor cambió mientras tanto, hay conflicto: queda el cambio más reciente.
   */
  base?: Record<string, unknown>;
  created_at: string;
  created_by: string;
  device_id: string | null;
  /** Credencial de licencia con la que se hizo (etapa 8B): el servidor acepta lo hecho sin conexión con ella. */
  lic_jti?: string;
  status: OutboxStatus;
  attempts: number;
  next_attempt_at: number;
  last_error?: string;
}

export interface SyncCursor {
  xid: string;
  id: string;
}

export const ZERO_UUID = '00000000-0000-0000-0000-000000000000';
export const INITIAL_CURSOR: SyncCursor = { xid: '0', id: ZERO_UUID };

export interface SyncStore {
  /** Operaciones pendientes del autor (y las compartidas, ver SHARED_PUSH_TABLES), en orden, que ya pueden reintentarse. */
  nextPendingOps(authorId: string, limit: number, now: number): Promise<OutboxOp[]>;
  /** Saca de la cola las operaciones ya aplicadas en el servidor. */
  markPushed(pushed: PushedOp[]): Promise<void>;
  markRetry(seq: number, error: string, nextAttemptAt: number): Promise<void>;
  markRejected(seq: number, error: string): Promise<void>;
  countOps(): Promise<{ pending: number; rejected: number }>;
  getCursor(businessId: string, table: SyncTable): Promise<SyncCursor | null>;
  /** Guarda filas del servidor y el nuevo cursor de forma atómica. */
  applyPulled(businessId: string, table: SyncTable, rows: Row[], cursor: SyncCursor): Promise<void>;
  /** Guarda filas del servidor sin tocar el cursor (p. ej. tras un rechazo). */
  applyRows(table: SyncTable, rows: Row[]): Promise<void>;
}

/** xid: transacción del servidor que aplicó la operación (para saber qué incluye el resumen de stock). */
/** refetch: el servidor dejó algún valor suyo (era más reciente); hay que traer la fila. */
export type PushResult = { status: 'ok'; xid?: string; refetch?: boolean } | { status: 'rejected'; error: string };

export interface PushedOp {
  op: OutboxOp;
  xid?: string;
}

export interface PullResult {
  rows: Row[];
  safeXid: string;
}

export interface SyncTransport {
  /** Usuario autenticado en el servidor ahora mismo (null si no hay sesión online). */
  currentUserId(): Promise<string | null>;
  /**
   * Envía un lote de operaciones en orden. Devuelve un resultado por operación (mismo orden).
   * Si falla el lote entero (red, servidor), lanza un error y el motor reintenta más tarde.
   */
  pushBatch(ops: OutboxOp[]): Promise<PushResult[]>;
  pull(businessId: string, table: SyncTable, cursor: SyncCursor, limit: number): Promise<PullResult>;
  /** Trae la versión actual de filas puntuales. */
  fetchRows(table: SyncTable, ids: string[]): Promise<Row[]>;
}

/** Error de transporte. retryable=true: problema de red/servidor, se reintenta más tarde. */
export class SyncError extends Error {
  constructor(
    message: string,
    readonly retryable: boolean,
  ) {
    super(message);
    this.name = 'SyncError';
  }
}
